import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import swal from "sweetalert";
import { AuthContext } from "../../../context/AuthContext";
import { saveUddyamaanAssessment, getUddyamaanAssessmentResult } from "../../../services/adminApiService/uddyamaanApi/uddyamaanApi";
import ConfirmModal from "../editProfile/ConfirmModal";
import { scoreAllSkillTests, maxSkillPoints, getSkillTestFields } from "./skillTestScoring";
import { scoreAllBatteryTests, maxBatteryPoints, getBatteryTestFields } from "./batteryTestScoring";

// Protsaahan's own assessment pipeline, entirely separate from the generic
// Uddyamaan fitness form (AssessmentFormModal). Flow: battery test happens
// at block/local_body (screening) AND again at district (verification);
// only once district's OWN battery test is submitted does the skill test
// step unlock, and only after that is the district ready to make its final
// selection. Each stage/step is its own backend submission (see
// UddyamaanApplicationService::saveProtsaahanAssessment) -- this modal
// mirrors that by showing exactly one editable section at a time.
const STAGE_LABELS = { 7: "Nyaya Panchayat", 6: "Block", 8: "Local Body", 3: "District" };

const emptyValues = (fields) =>
  fields.reduce((acc, f) => ({ ...acc, [f.key]: '', [`${f.key}_points`]: '' }), {});

const ProtsaahanAssessmentModal = ({ show, onHide, application }) => {
  const { userData } = useContext(AuthContext);
  const stageLabel = STAGE_LABELS[Number(userData?.user_level)];
  const [form, setForm] = useState({ notes: '' });
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [batteryDone, setBatteryDone] = useState(false);
  const [skillDone, setSkillDone] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const batteryFields = getBatteryTestFields(application?.sport, application?.gender);
  const skillFields = getSkillTestFields(application?.sport, application?.gender);

  // Battery test runs at every Protsaahan stage; skill test is
  // district-only, and only reachable once district's own battery test row
  // exists (batteryDone here is always scoped to the acting stage — see
  // fetch below).
  const showBatteryTest = batteryFields.length > 0;
  const showSkillTest = stageLabel === 'District' && skillFields.length > 0 && batteryDone;

  const phase = !showBatteryTest && !showSkillTest
    ? 'unavailable'
    : (!batteryDone ? 'battery' : (stageLabel === 'District' && !skillDone ? 'skill' : 'done'));

  useEffect(() => {
    setShowConfirm(false);
    if (!show || !application?.id) return;
    setFetching(true);
    getUddyamaanAssessmentResult(application.id)
      .then(res => {
        const body = res?.data;
        const d = body?.data;
        const battery = body?.success ? d?.battery_test : null;
        const skill = body?.success ? d?.skill_test : null;
        setBatteryDone(!!battery);
        setSkillDone(!!skill);
        setForm({
          notes: battery?.notes ?? skill?.notes ?? '',
          ...batteryFields.reduce((acc, f) => ({
            ...acc,
            [f.key]: battery?.[`${f.key}_raw`] ?? '',
            [`${f.key}_points`]: battery?.[`${f.key}_points`] ?? '',
          }), {}),
          ...skillFields.reduce((acc, f) => ({
            ...acc,
            [f.key]: skill?.[`${f.key}_raw`] ?? '',
            [`${f.key}_points`]: skill?.[`${f.key}_points`] ?? '',
          }), {}),
        });
      })
      .catch(() => {
        setBatteryDone(false);
        setSkillDone(false);
        setForm({ notes: '', ...emptyValues(batteryFields), ...emptyValues(skillFields) });
      })
      .finally(() => setFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, application?.id]);

  const batteryRawSignature = batteryFields.map(f => form[f.key]).join('|');
  useEffect(() => {
    if (phase !== 'battery') return;
    const computed = scoreAllBatteryTests(application?.sport, application?.gender, application?.dob, form);
    delete computed.total_points;
    setForm(f => {
      const changed = Object.keys(computed).some(k => f[k] !== computed[k]);
      return changed ? { ...f, ...computed } : f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, application?.sport, application?.gender, application?.dob, batteryRawSignature]);

  const skillRawSignature = skillFields.map(f => form[f.key]).join('|');
  useEffect(() => {
    if (phase !== 'skill') return;
    const computed = scoreAllSkillTests(application?.sport, application?.gender, application?.dob, form);
    delete computed.total_points;
    setForm(f => {
      const changed = Object.keys(computed).some(k => f[k] !== computed[k]);
      return changed ? { ...f, ...computed } : f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, application?.sport, application?.gender, application?.dob, skillRawSignature]);

  const set = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const handleConfirmedSave = async () => {
    setSaving(true);
    try {
      await saveUddyamaanAssessment(application.id, form);
      swal("Saved", `${phase === 'skill' ? 'Skill' : 'Battery'} test saved successfully.`, "success");
      onHide(true);
    } catch (err) {
      swal("Error", err?.response?.data?.message || "Failed to save assessment.", "error");
    } finally {
      setSaving(false);
    }
  };

  const editable = phase === 'battery' || phase === 'skill';

  const num = (name, extra = {}) => ({
    name, value: form[name], onChange: editable ? set : undefined,
    type: "number", min: "0", step: "0.01", className: "form-control",
    readOnly: !editable,
    ...extra,
  });

  const pt = (name, extra = {}) => ({
    name, value: form[name],
    type: "number", min: "0", max: "3", step: "1", className: "form-control",
    readOnly: true, disabled: true,
    placeholder: "0",
    ...extra,
  });

  const batteryTotalPoints = batteryFields.reduce((sum, f) => sum + (Number(form[`${f.key}_points`]) || 0), 0);
  const batteryMaxPoints = maxBatteryPoints(application?.sport, application?.gender);
  const skillTotalPoints = skillFields.reduce((sum, f) => sum + (Number(form[`${f.key}_points`]) || 0), 0);
  const skillMaxPoints = maxSkillPoints(application?.sport, application?.gender);

  const title = {
    unavailable: 'No Assessment Chart',
    battery: 'Conduct Battery Test',
    skill: 'Conduct Skill Test',
    done: 'View Assessment',
  }[phase];

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18, fontWeight: 600 }}>
          {title}{stageLabel ? ` (${stageLabel})` : ''} — {application?.name || ""}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleFormSubmit}>
        <Modal.Body>
          {fetching ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : phase === 'unavailable' ? (
            <p className="text-muted mb-0">No battery/skill test chart is available for this sport/gender yet.</p>
          ) : (
            <Row className="g-3">
              {(phase === 'battery' || batteryDone) && (
                <>
                  <Col xs={12}>
                    <h6 className="fw-semibold mb-0" style={{ color: '#6366F1' }}>Battery Test</h6>
                  </Col>
                  {batteryFields.map(({ key, label, unit }) => (
                    <Col md={6} key={key}>
                      <Form.Label style={{ fontWeight: 500 }}>{label}</Form.Label>
                      <div className="input-group mb-2">
                        <input {...num(key)} placeholder="0.00" disabled={phase !== 'battery'} readOnly={phase !== 'battery'} />
                        <span className="input-group-text">{unit}</span>
                      </div>
                      <Form.Label className="text-muted" style={{ fontSize: 13 }}>Points (0-3)</Form.Label>
                      <input {...pt(`${key}_points`)} />
                    </Col>
                  ))}
                  <Col xs={12}>
                    <div
                      className="d-flex justify-content-between align-items-center p-3"
                      style={{ background: "#EEF2FF", borderRadius: 8, border: "1px solid #C7D2FE" }}
                    >
                      <span style={{ fontWeight: 600, color: "#4338CA" }}>Battery Test Total</span>
                      <span style={{ fontWeight: 700, fontSize: 20, color: "#4338CA" }}>{batteryTotalPoints} / {batteryMaxPoints}</span>
                    </div>
                  </Col>
                </>
              )}

              {showSkillTest && (
                <>
                  <Col xs={12}>
                    <h6 className="fw-semibold mb-0 mt-2" style={{ color: '#6366F1' }}>Skill Test</h6>
                  </Col>
                  {skillFields.map(({ key, label, unit, inputProps }) => (
                    <Col md={6} key={key}>
                      <Form.Label style={{ fontWeight: 500 }}>{label}</Form.Label>
                      <div className="input-group mb-2">
                        <input {...num(key, inputProps)} placeholder="0.00" disabled={phase !== 'skill'} readOnly={phase !== 'skill'} />
                        <span className="input-group-text">{unit}</span>
                      </div>
                      <Form.Label className="text-muted" style={{ fontSize: 13 }}>Points (0-3)</Form.Label>
                      <input {...pt(`${key}_points`, { step: "0.5" })} />
                    </Col>
                  ))}
                  <Col xs={12}>
                    <div
                      className="d-flex justify-content-between align-items-center p-3"
                      style={{ background: "#EEF2FF", borderRadius: 8, border: "1px solid #C7D2FE" }}
                    >
                      <span style={{ fontWeight: 600, color: "#4338CA" }}>Skill Test Total</span>
                      <span style={{ fontWeight: 700, fontSize: 20, color: "#4338CA" }}>{skillTotalPoints} / {skillMaxPoints}</span>
                    </div>
                  </Col>
                </>
              )}

              <Col xs={12}>
                <Form.Label style={{ fontWeight: 500 }}>Notes (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={form.notes}
                  onChange={editable ? set : undefined}
                  readOnly={!editable}
                  placeholder="Any additional observations..."
                />
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex gap-2 w-100">
            <Button variant="outline-secondary" className="flex-fill" onClick={() => onHide(false)} disabled={saving}>
              {editable ? 'Cancel' : 'Close'}
            </Button>
            {editable && (
              <Button
                type="submit"
                className="flex-fill"
                disabled={saving || fetching}
                style={{ backgroundColor: "#7C3AED", border: "none" }}
              >
                {saving ? "Saving..." : phase === 'skill' ? 'Save Skill Test' : 'Save Battery Test'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Form>
      <ConfirmModal
        type="save-assessment"
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmedSave}
      />
    </Modal>
  );
};

export default ProtsaahanAssessmentModal;
